import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import LiveMap from './pages/LiveMap';
import Patrols from './pages/Patrols';
import PatrolDetail from './pages/PatrolDetail';
import Routes_ from './pages/Routes';
import Guards from './pages/Guards';
import Sites from './pages/Sites';
import Incidents from './pages/Incidents';
import Reports from './pages/Reports';
import GuardSimulator from './pages/GuardSimulator';
import Shifts from './pages/Shifts';
import Geofencing from './pages/Geofencing';
import Settings from './pages/Settings';
import ReportBuilder from './pages/ReportBuilder';

// Protect routes — redirect to login if not authenticated
function PrivateRoute({ children }) {
  const { token } = useAuth();
  return token ? children : <Navigate to="/login" replace />;
}

// Role-based route — redirect to dashboard if insufficient role
function RoleRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              style: { borderRadius: 10, fontSize: 14 },
              success: { duration: 3000 },
              error: { duration: 4000 },
            }}
          />
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
              {/* All roles */}
              <Route index element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
              <Route path="patrols" element={<ErrorBoundary><Patrols /></ErrorBoundary>} />
              <Route path="patrols/:id" element={<ErrorBoundary><PatrolDetail /></ErrorBoundary>} />
              <Route path="incidents" element={<ErrorBoundary><Incidents /></ErrorBoundary>} />
              <Route path="simulator" element={<ErrorBoundary><GuardSimulator /></ErrorBoundary>} />

              {/* Admin + Supervisor only */}
              <Route path="map" element={
                <RoleRoute roles={['admin', 'supervisor']}>
                  <ErrorBoundary><LiveMap /></ErrorBoundary>
                </RoleRoute>
              } />
              <Route path="routes" element={
                <RoleRoute roles={['admin', 'supervisor']}>
                  <ErrorBoundary><Routes_ /></ErrorBoundary>
                </RoleRoute>
              } />
              <Route path="sites" element={
                <RoleRoute roles={['admin', 'supervisor']}>
                  <ErrorBoundary><Sites /></ErrorBoundary>
                </RoleRoute>
              } />
              <Route path="guards" element={
                <RoleRoute roles={['admin', 'supervisor']}>
                  <ErrorBoundary><Guards /></ErrorBoundary>
                </RoleRoute>
              } />
              <Route path="reports" element={
                <RoleRoute roles={['admin', 'supervisor']}>
                  <ErrorBoundary><Reports /></ErrorBoundary>
                </RoleRoute>
              } />
              <Route path="report-builder" element={
                <RoleRoute roles={['admin', 'supervisor']}>
                  <ErrorBoundary><ReportBuilder /></ErrorBoundary>
                </RoleRoute>
              } />
              <Route path="shifts" element={
                <RoleRoute roles={['admin', 'supervisor']}>
                  <ErrorBoundary><Shifts /></ErrorBoundary>
                </RoleRoute>
              } />
              <Route path="geofencing" element={
                <RoleRoute roles={['admin', 'supervisor']}>
                  <ErrorBoundary><Geofencing /></ErrorBoundary>
                </RoleRoute>
              } />
              <Route path="settings" element={
                <RoleRoute roles={['admin']}>
                  <ErrorBoundary><Settings /></ErrorBoundary>
                </RoleRoute>
              } />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
